import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button, cn } from "@eduviaos/ui-kit";
import { ResultHero } from "../../components/motion/ResultHero";
import { Reveal, SectionCard } from "../../components/Section";
import type { ZiggyAge } from "../../lib/ziggy";
import { EASE_OUT, FLOW_ENTER } from "../../lib/motion";
import { useLearnLocale } from "../../lib/i18n/useLearnLocale";
import { apiErrorMessage } from "../../lib/apiErrorCopy";
import { useSubmitPracticeTest } from "../../lib/learning/hooks";
import type {
  DiagnosticSession,
  PracticeTestResult,
  PracticeTestSession,
} from "../../lib/learning/types";

/**
 * On-demand practice test runner (SC-11 + SC-13 motion).
 * Question loop matches DiagnosticRunner; result view staggers weakness areas.
 */
export function PracticeTestRunner({
  session,
  ziggyAge = "grown",
  onClose,
}: {
  session: PracticeTestSession;
  ziggyAge?: ZiggyAge;
  onClose: () => void;
}) {
  const submitMut = useSubmitPracticeTest();
  const [result, setResult] = React.useState<PracticeTestResult | null>(null);

  if (result) {
    return <PracticeTestResultView result={result} ziggyAge={ziggyAge} onDone={onClose} />;
  }

  const diagnosticSession: DiagnosticSession = {
    id: session.id,
    pathway_id: session.pathway_id,
    leaf_id: null,
    status: session.status,
    questions: session.questions,
    created_at: session.created_at,
  };

  return (
    <PracticeQuizBridge
      session={diagnosticSession}
      onClose={onClose}
      submitting={submitMut.isPending}
      onSubmit={async (answers) => {
        const res = await submitMut.mutateAsync({ sessionId: session.id, answers });
        setResult(res);
      }}
    />
  );
}

function PracticeQuizBridge({
  session,
  onClose,
  submitting,
  onSubmit,
}: {
  session: DiagnosticSession;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (
    answers: { question_id: string; selected_option_id: string | null }[],
  ) => Promise<void>;
}) {
  const reduce = useReducedMotion();
  const { strings: s, locale } = useLearnLocale();
  const [answers, setAnswers] = React.useState<Record<string, string | null>>({});
  const [index, setIndex] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);

  const questions = session.questions;
  const total = questions.length;
  const question = questions[Math.min(index, total - 1)];
  const isLast = index >= total - 1;

  if (!question) {
    return (
      <SectionCard className="space-y-3 text-center">
        <p className="text-sm font-semibold">{s.runnerNoQuestionsTest}</p>
        <Button variant="outline" onClick={onClose}>
          {s.back}
        </Button>
      </SectionCard>
    );
  }

  const answered = question.id in answers;
  const selected = answers[question.id] ?? null;
  const progress = Math.round(((index + 1) / total) * 100);

  async function finish() {
    setError(null);
    try {
      await onSubmit(
        questions.map((q) => ({
          question_id: q.id,
          selected_option_id: answers[q.id] ?? null,
        })),
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 503
          ? s.runnerScoreBusy
          : apiErrorMessage(err, locale, s, s.runnerTestSubmitFailed),
      );
    }
  }

  return (
    <div className="space-y-4">
      {/* Without a visible counter the test felt open-ended — learners had no
          way to judge how much was left before committing to finish. */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (index === 0 ? onClose() : setIndex((i) => i - 1))}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm font-medium transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="size-4" />
          {s.back}
        </button>
        <span className="text-muted-foreground text-xs font-semibold tabular-nums">
          {index + 1} / {total}
        </span>
      </div>

      <div
        className="bg-muted h-2 overflow-hidden rounded-full"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={index + 1}
        aria-label={s.quizProgressAria(index + 1, total)}
      >
        <motion.div
          className="bg-primary h-full rounded-full"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: reduce ? 0 : 0.3, ease: EASE_OUT }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={reduce ? false : { opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, x: -16 }}
          transition={reduce ? { duration: 0 } : FLOW_ENTER}
          className="space-y-4"
        >
          <SectionCard className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="bg-primary/12 text-primary rounded-full px-2.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wide">
                {question.subject}
              </span>
              <span className="text-muted-foreground text-xs">{question.topic}</span>
            </div>
            <p id={`pt-${question.id}`} className="text-sm font-semibold leading-relaxed">
              {question.prompt}
            </p>
          </SectionCard>

          <fieldset className="space-y-2" aria-labelledby={`pt-${question.id}`}>
            {question.options.map((opt) => {
              const active = selected === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: opt.id }))}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-150 active:scale-[0.99]",
                    active
                      ? "border-primary bg-primary/8 font-semibold"
                      : "border-border/60 hover:bg-muted/40",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-5 shrink-0 place-items-center rounded-full border",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border",
                    )}
                  >
                    {active && <Check className="size-3" />}
                  </span>
                  <span className="min-w-0">{opt.label}</span>
                </button>
              );
            })}
          </fieldset>
        </motion.div>
      </AnimatePresence>

      {error && (
        <p role="alert" className="text-destructive text-center text-xs font-medium">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          disabled={submitting}
          onClick={() => {
            setAnswers((prev) => ({ ...prev, [question.id]: null }));
            if (!isLast) setIndex((i) => i + 1);
          }}
        >
          {s.skip}
        </Button>
        <Button
          className="flex-[2] active:scale-[0.97]"
          disabled={submitting || (!isLast && !answered)}
          onClick={() => (isLast ? void finish() : setIndex((i) => i + 1))}
        >
          {submitting ? (
            <>
              <Loader2 className="mr-1.5 size-4 animate-spin" /> {s.scoring}
            </>
          ) : isLast ? (
            s.runnerFinishTest
          ) : (
            s.runnerNext
          )}
        </Button>
      </div>
    </div>
  );
}

function PracticeTestResultView({
  result,
  ziggyAge,
  onDone,
}: {
  result: PracticeTestResult;
  ziggyAge: ZiggyAge;
  onDone: () => void;
}) {
  const { strings: s } = useLearnLocale();
  const report = result.weakness_report;
  const pct = Math.round(result.score * 100);

  return (
    <div className="space-y-4">
      <ResultHero
        pose={pct >= 80 ? "excited" : "thinking"}
        ziggyAge={ziggyAge}
        title={report.headline}
        subtitle={`${s.runnerCorrectOfAsked(result.correct, result.asked)} · ${report.summary}`}
      />

      {report.focus_areas.length > 0 && (
        <section className="space-y-2">
          <Reveal>
            <h3 className="font-display text-base font-semibold">{s.focusNext}</h3>
          </Reveal>
          {report.focus_areas.map((area, i) => (
            <Reveal key={`${area.subject}:${area.topic}`} index={i + 1}>
              <SectionCard className="space-y-1 py-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{area.topic}</p>
                  <span className="text-muted-foreground text-[0.65rem] font-semibold uppercase">
                    {area.severity}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs">{area.advice}</p>
              </SectionCard>
            </Reveal>
          ))}
        </section>
      )}

      <Reveal index={report.focus_areas.length + 2}>
        <Button className="w-full active:scale-[0.97]" onClick={onDone}>
          {s.done}
        </Button>
      </Reveal>
    </div>
  );
}
